<template>
  <div ref="container" />
</template>

<script lang="ts">
let loadPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="js.hcaptcha.com"]');

    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }

    window.onloadHcaptchaCallback = () => resolve();
    const script = document.createElement("script");
    script.src =
      "https://js.hcaptcha.com/1/api.js?onload=onloadHcaptchaCallback&render=explicit";
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);
  });

  return loadPromise;
}
</script>

<script setup lang="ts">
import { useTemplateRef, watch } from "vue";

const props = defineProps<{
  siteKey: string;
  theme?: "light" | "dark" | "contrast";
  size?: "normal" | "compact" | "invisible";
  tabindex?: number;
  language?: string;
}>();

const emit = defineEmits<{
  callback: [response: string];
  errorCallback: [response: string];
  expiredCallback: [];
}>();

const container = useTemplateRef("container");

watch(container, async (node, _prevNode, onCleanup) => {
  if (!node) {
    return;
  }

  const controller = new AbortController();
  const signal = controller.signal;

  onCleanup(() => {
    controller.abort();
  });

  await loadScript();
  if (signal.aborted) {
    return;
  }

  const id = window.hcaptcha!.render(node, {
    sitekey: props.siteKey,
    callback: (response: string) => emit("callback", response),
    "expired-callback": () => emit("expiredCallback"),
    "error-callback": (response: string) => emit("errorCallback", response),
    theme: props.theme,
    size: props.size,
    tabindex: props.tabindex,
    language: props.language,
  });

  signal.addEventListener("abort", () => {
    window.hcaptcha!.remove(id);
  });
});
</script>
